import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import type { ContactFieldsQueryValue } from "./fieldFilters";

const apiBase = config.api.API_URL;

export type ContactTag = {
  id: number;
  name: string;
};

export type Contact = {
  id: number;
  email: string;
  name: string | null;
  /** DDI só dígitos (ex. 55) — LAC-102. */
  country_code?: string | null;
  phone_number?: string | null;
  /** Conveniência: country_code + phone_number. */
  phone?: string | null;
  status: string;
  /** Tag auto de hard bounce — badge na lista sem jargão SES. */
  hard_bounce_flagged?: boolean;
  tags: ContactTag[];
  /** slug → valor tipado (DOC 03 § 4.7 · LAC-079). */
  fields?: Record<string, string | number | boolean | string[] | null>;
  deleted_at?: string | null;
  created_at?: string;
};

export type PaginatedMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type ContactsListResponse = ApiSuccess<Contact[]> & {
  meta: PaginatedMeta;
  links?: unknown;
};

export const getContacts = (params?: {
  search?: string;
  /** Parcial; máscara ok — API stripa para dígitos (LAC-108). */
  phone?: string;
  /** active | unsubscribed | quarantine | suppressed */
  status?: string;
  /** only = lixeira; with = ativos + excluídos */
  trashed?: "only" | "with";
  /** Compat: um id. Preferir tag_ids. */
  tag_id?: number;
  /** Filtro multi — GET ?tag_ids[]=1&tag_ids[]=2 */
  tag_ids?: number[];
  /** and = todas as tags; or = qualquer (default) */
  tag_match?: "and" | "or";
  /** Filtro tipado — GET ?fields[slug]=valor ou nested op (LAC-103). */
  fields?: Record<string, ContactFieldsQueryValue>;
  /** 1 = só contatos com tag auto de hard bounce (LAC-117 polish). */
  hard_bounce_flagged?: 1 | 0 | boolean;
  page?: number;
  per_page?: number;
}): Promise<ContactsListResponse> => {
  return axios.get(apiBase + "/contacts", { params });
};

export const createContact = (payload: {
  email: string;
  name?: string;
  country_code?: string | null;
  phone_number?: string | null;
  phone?: string | null;
  tag_ids?: number[];
  fields?: Record<string, string | boolean | string[] | null>;
}): Promise<ApiSuccess<Contact>> => {
  return axios.post(apiBase + "/contacts", payload);
};

export const updateContact = (
  id: number,
  payload: {
    email?: string;
    name?: string | null;
    country_code?: string | null;
    phone_number?: string | null;
    phone?: string | null;
    tag_ids?: number[];
    fields?: Record<string, string | boolean | string[] | null>;
  }
): Promise<ApiSuccess<Contact>> => {
  return axios.patch(apiBase + "/contacts/" + id, payload);
};

export const deleteContact = (id: number): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/contacts/" + id);
};

export const restoreContact = (id: number): Promise<ApiSuccess<Contact>> => {
  return axios.post(apiBase + "/contacts/" + id + "/restore");
};

/** Unsubscribed → active (painel). Suppressed não passa por aqui. */
export const reactivateContact = (id: number): Promise<ApiSuccess<Contact>> => {
  return axios.post(apiBase + "/contacts/" + id + "/reactivate");
};

/** Contato pending_doi → reenvia e-mail de confirmação (fila mail). */
export const resendDoiContact = (id: number): Promise<ApiSuccess<null>> => {
  return axios.post(apiBase + "/contacts/" + id + "/doi-resend");
};

export type Tag = {
  id: number;
  name: string;
  /** Contatos vinculados (lista `/tags`); opcional nos selects. */
  contacts_count?: number;
};

type TagsListResponse = ApiSuccess<Tag[]> & {
  meta: PaginatedMeta;
};

export const getTags = (params?: {
  search?: string;
  sort?: "name" | "usage";
  page?: number;
  per_page?: number;
}): Promise<TagsListResponse> => {
  return axios.get(apiBase + "/tags", { params });
};

/** Opções para react-select/async — busca paginada sem carregar catálogo inteiro. */
export async function loadTagOptions(
  input: string
): Promise<Array<{ value: number; label: string; contacts_count?: number }>> {
  const res = await getTags({
    search: input.trim() || undefined,
    per_page: 50,
    page: 1,
    sort: "name",
  });
  if (!res?.success) {
    return [];
  }
  return (res.data || []).map((tag) => ({
    value: tag.id,
    label:
      typeof tag.contacts_count === "number"
        ? `${tag.name} (${tag.contacts_count})`
        : tag.name,
    contacts_count: tag.contacts_count,
  }));
}

/** Resolve nomes para deep-link ?tag_ids= sem carregar catálogo inteiro. */
export async function resolveTagOptionsByIds(
  ids: number[]
): Promise<Array<{ value: number; label: string }>> {
  const wanted = new Set(ids.filter((id) => id > 0));
  if (wanted.size === 0) {
    return [];
  }

  const found = new Map<number, string>();
  let page = 1;
  let lastPage = 1;

  do {
    const res = await getTags({ page, per_page: 100, sort: "name" });
    if (!res?.success) {
      break;
    }
    for (const tag of res.data || []) {
      if (wanted.has(tag.id)) {
        found.set(tag.id, tag.name);
      }
    }
    lastPage = res.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage && found.size < wanted.size);

  return ids
    .filter((id) => wanted.has(id))
    .map((id) => ({
      value: id,
      label: found.get(id) ?? `#${id}`,
    }));
}

/**
 * Catálogo completo de tags (todas as páginas).
 * Por quê: `GET /tags` pagina (max 100); selects com só a 1ª página escondem tags novas
 * quando a conta tem >100 marcadores (legado). Não há cache HTTP — era truncamento.
 * Teto 50 páginas (= 5k) evita loop infinito em bases anômalas.
 */
export const fetchAllTags = async (search?: string): Promise<Tag[]> => {
  const all: Tag[] = [];
  let page = 1;
  let lastPage = 1;
  const perPage = 100;
  const maxPages = 50;

  do {
    const res = await getTags({
      page,
      per_page: perPage,
      search: search || undefined,
    });
    if (!res?.success) {
      break;
    }
    all.push(...(res.data || []));
    lastPage = res.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage && page <= maxPages);

  return all;
};

export const createTag = (payload: {
  name: string;
}): Promise<ApiSuccess<Tag>> => {
  return axios.post(apiBase + "/tags", payload);
};

export const updateTag = (
  id: number,
  payload: { name: string }
): Promise<ApiSuccess<Tag>> => {
  return axios.patch(apiBase + "/tags/" + id, payload);
};

export const deleteTag = (id: number): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/tags/" + id);
};

export const mergeTags = (payload: {
  source_ids: number[];
  target_id: number;
}): Promise<ApiSuccess<Tag>> => {
  return axios.post(apiBase + "/tags/merge", payload);
};

export type TagsMergePreview = {
  unique_contacts_count: number;
  source_contacts_sum: number;
};

export const previewMergeTags = (payload: {
  source_ids: number[];
  target_id: number;
}): Promise<ApiSuccess<TagsMergePreview>> => {
  return axios.post(apiBase + "/tags/merge-preview", payload);
};

export const bulkDeleteTags = (payload: {
  ids: number[];
}): Promise<ApiSuccess<{ deleted_count: number }>> => {
  return axios.post(apiBase + "/tags/bulk-delete", payload);
};

/** Regras v1 de segmento (DOC 03 § 4.8.1). */
export type SegmentRules = {
  version: 1;
  match: "all" | "any";
  conditions: SegmentCondition[];
};

export type SegmentCondition =
  | { type: "status"; op: "eq"; value: string }
  | {
      type: "tag";
      op: "in" | "not_in";
      tag_ids: number[];
      tag_match?: "and" | "or";
    }
  | {
      type: "field";
      slug: string;
      op: "eq" | "contains" | "gte" | "lte" | "between" | "in";
      value: string | number | boolean | string[];
      value_to?: string | number;
    }
  | {
      type: "engagement";
      event: "open" | "click";
      op: "in_last_days" | "not_in_last_days";
      days: number;
      /** Opcional: restringe ao disparo (LAC-119). */
      campaign_send_id?: number;
    };

export type Segment = {
  id: number;
  name: string;
  rules: SegmentRules;
  created_at?: string;
  updated_at?: string;
};

type SegmentsListResponse = ApiSuccess<Segment[]> & {
  meta: PaginatedMeta;
};

export const getSegments = (params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<SegmentsListResponse> => {
  return axios.get(apiBase + "/segments", { params });
};

export const getSegment = (id: number): Promise<ApiSuccess<Segment>> => {
  return axios.get(apiBase + "/segments/" + id);
};

/** Catálogo completo para o seletor do disparo (mesma ideia do fetchAllTags). */
export const fetchAllSegments = async (search?: string): Promise<Segment[]> => {
  const all: Segment[] = [];
  let page = 1;
  let lastPage = 1;
  const perPage = 100;
  const maxPages = 50;

  do {
    const res = await getSegments({
      page,
      per_page: perPage,
      search: search || undefined,
    });
    if (!res?.success) {
      break;
    }
    all.push(...(res.data || []));
    lastPage = res.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage && page <= maxPages);

  return all;
};

export const createSegment = (payload: {
  name: string;
  rules: SegmentRules;
}): Promise<ApiSuccess<Segment>> => {
  return axios.post(apiBase + "/segments", payload);
};

export const updateSegment = (
  id: number,
  payload: { name?: string; rules?: SegmentRules }
): Promise<ApiSuccess<Segment>> => {
  return axios.patch(apiBase + "/segments/" + id, payload);
};

export const deleteSegment = (id: number): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/segments/" + id);
};

export const estimateSegment = (
  id: number
): Promise<ApiSuccess<{ count: number }>> => {
  return axios.post(apiBase + "/segments/" + id + "/estimate");
};

export const estimateSegmentRules = (
  rules: SegmentRules
): Promise<ApiSuccess<{ count: number }>> => {
  return axios.post(apiBase + "/segments/estimate", { rules });
};

export type ImportSessionQueued = {
  id: number;
  status: string;
  job_status: string;
};

export type ImportSessionProgress = {
  id: number;
  status: "pending" | "processing" | "completed" | "failed" | string;
  is_preview?: boolean;
  total_rows: number;
  processed_rows: number;
  imported_rows: number;
  duplicated_rows: number;
  failed_rows: number;
  rejected_count?: number;
  /** Linhas puladas por lista de bloqueio (separado de inválidos). */
  blocklisted_rows?: number;
  /** Higiene F1 — DOC 04 § 4.5.2 · LAC-081. */
  syntax_error_rows?: number;
  no_mx_rows?: number;
  suppressed_rows?: number;
  email_suppressed_rows?: number;
  domain_suppressed_rows?: number;
  /** Score % composto (LAC-125) — orientação no resumo. */
  hygiene_risk_rows?: number;
  hygiene_risk_percent?: number;
  hygiene_band?: "good" | "attention" | "risk";
  progress_percent: number;
  failure_message: string | null;
  /** LAC-029: CSV de rejeitados disponível para download. */
  has_rejected_csv?: boolean;
  preview?: ImportPreviewResult | null;
  created_at?: string;
  updated_at?: string;
};

/** Dry-run pré-fila (LAC-029). */
export type ImportPreviewResult = {
  total_rows: number;
  scanned_rows: number;
  truncated: boolean;
  would_import_rows: number;
  syntax_error_rows: number;
  no_mx_rows: number;
  email_suppressed_rows: number;
  domain_suppressed_rows: number;
  suppressed_rows: number;
  blocklisted_rows: number;
  rejected_rows?: number;
  rejected_beyond_sample?: number;
  sample_rejects: Array<{
    row: number;
    email: string;
    reason: string;
    reason_label?: string;
  }>;
};

export type ImportPreviewAsyncAccepted = {
  async: true;
  id: number;
  status: string;
  job_status: string;
  total_rows?: number;
};

/** Saúde da fila Redis + heartbeat do worker (LAC-028). */
export type QueueHealth = {
  import_pending: number | null;
  send_pending: number | null;
  send_p0_pending: number | null;
  worker_ok: boolean | null;
  worker_heartbeat_at: string | null;
  source: string;
};

/**
 * Upload CSV/TXT — multipart; resposta 202 (DOC 06 § 2.2).
 * FormData: não forçar application/json do default axios.
 */
export const importContactsCsv = (
  file: File,
  options?: {
    tagIds?: number[];
    /** Chave = email|name|slug → valor = header do CSV. */
    columnMap?: Record<string, string>;
  }
): Promise<ApiSuccess<ImportSessionQueued>> => {
  const form = new FormData();
  form.append("file", file);
  (options?.tagIds || []).forEach((id) => {
    form.append("tag_ids[]", String(id));
  });
  const columnMap = options?.columnMap;
  if (columnMap?.email) {
    Object.entries(columnMap).forEach(([key, header]) => {
      if (header) {
        form.append(`column_map[${key}]`, header);
      }
    });
  }

  return axios.post(apiBase + "/contacts/import", form, {
    // Remove application/json default — o browser define o boundary do multipart.
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    ],
  });
};

/** Simulação pré-fila — mesma higiene, sem gravar (LAC-029). */
export const previewContactsImport = (
  file: File,
  options?: {
    columnMap?: Record<string, string>;
  }
): Promise<ApiSuccess<ImportPreviewResult | ImportPreviewAsyncAccepted>> => {
  const form = new FormData();
  form.append("file", file);
  const columnMap = options?.columnMap;
  if (columnMap?.email) {
    Object.entries(columnMap).forEach(([key, header]) => {
      if (header) {
        form.append(`column_map[${key}]`, header);
      }
    });
  }

  return axios.post(apiBase + "/contacts/import/preview", form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    ],
  });
};

/** Dry-run → CSV completo de rejeitados (cap do preview no servidor). */
export const downloadImportPreviewRejectedCsv = async (
  file: File,
  options?: {
    columnMap?: Record<string, string>;
    filename?: string;
  }
): Promise<void> => {
  const form = new FormData();
  form.append("file", file);
  const columnMap = options?.columnMap;
  if (columnMap?.email) {
    Object.entries(columnMap).forEach(([key, header]) => {
      if (header) {
        form.append(`column_map[${key}]`, header);
      }
    });
  }

  const data = await axios.post(
    apiBase + "/contacts/import/preview-rejected.csv",
    form,
    {
      responseType: "blob",
      transformRequest: [
        (body, headers) => {
          if (body instanceof FormData && headers) {
            delete (headers as Record<string, unknown>)["Content-Type"];
          }
          return body;
        },
      ],
    }
  );

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download =
    options?.filename || "importacao_simulacao_rejeitados.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const getImportSession = (
  id: number
): Promise<ApiSuccess<ImportSessionProgress>> => {
  return axios.get(apiBase + "/imports/" + id);
};

export const getImportSessions = (
  perPage = 10
): Promise<ApiSuccess<ImportSessionProgress[]>> => {
  return axios.get(apiBase + "/imports", { params: { per_page: perPage } });
};

/** LAC-028 — sinal se o worker está vivo (evita “travou” falso). */
export const getQueueHealth = (): Promise<ApiSuccess<QueueHealth>> => {
  return axios.get(apiBase + "/ops/queue-health");
};

/** Download CSV de linhas rejeitadas (LAC-029). */
export const downloadImportRejectedCsv = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const data = await axios.get(apiBase + "/imports/" + id + "/rejected.csv", {
    responseType: "blob",
  });

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const today = new Date().toISOString().slice(0, 10);
  anchor.download = fallbackFilename || `importacao_rejeitados_${today}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/** Filtros do export = mesmos da listagem (DOC 06 · LAC-027). */
export type ContactExportFilters = {
  search?: string;
  phone?: string;
  status?: string;
  trashed?: "only" | "with";
  tag_ids?: number[];
  tag_match?: "and" | "or";
  fields?: Record<string, ContactFieldsQueryValue>;
  /** Só e-mails inválidos (tag auto hard bounce). */
  hard_bounce_flagged?: 1 | 0 | boolean;
  /** Export dos elegíveis de higiene — DOC 03 § 4.15 · LAC-129 U2. */
  mode?: "inactive_hygiene";
  days?: number;
};

export type ExportSessionQueued = {
  id: number;
  status: string;
  job_status: string;
};

export type ExportSessionProgress = {
  id: number;
  status: "pending" | "processing" | "completed" | "failed" | string;
  total_rows: number;
  processed_rows: number;
  progress_percent: number;
  download_ready: boolean;
  filters?: ContactExportFilters | null;
  failure_message: string | null;
  created_at?: string;
  updated_at?: string;
};

/** POST 202 — Job na fila `import`; UI faz polling em getExportSession. */
export const startContactsExport = (
  filters: ContactExportFilters = {}
): Promise<ApiSuccess<ExportSessionQueued>> => {
  return axios.post(apiBase + "/contacts/export", filters);
};

export const getExportSession = (
  id: number
): Promise<ApiSuccess<ExportSessionProgress>> => {
  return axios.get(apiBase + "/exports/" + id);
};

export const getExportSessions = (
  perPage = 10
): Promise<ApiSuccess<ExportSessionProgress[]>> => {
  return axios.get(apiBase + "/exports", { params: { per_page: perPage } });
};

/**
 * Download CSV com JWT (blob) — mesma ideia do export de cupons SA.
 * Nome amigável no browser; Content-Disposition do servidor pode não chegar via interceptor.
 */
export const downloadContactsExportCsv = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const data = await axios.get(apiBase + "/exports/" + id + "/download", {
    responseType: "blob",
  });

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const today = new Date().toISOString().slice(0, 10);
  anchor.download = fallbackFilename || `contatos_${today}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/** Entrada da lista de bloqueio do tenant (DOC 03 § 4.9 · LAC-037). */
export type BlocklistEntry = {
  id: number;
  type: "email" | "domain";
  value: string;
  reason: string | null;
  created_by_user_id?: number | null;
  created_at?: string;
  updated_at?: string;
};

type BlocklistListResponse = ApiSuccess<BlocklistEntry[]> & {
  meta: PaginatedMeta;
};

export const getBlocklist = (params?: {
  type?: "email" | "domain";
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<BlocklistListResponse> => {
  return axios.get(apiBase + "/blocklist", { params });
};

export const createBlocklistEntry = (payload: {
  type: "email" | "domain";
  value: string;
  reason?: string | null;
}): Promise<ApiSuccess<BlocklistEntry>> => {
  return axios.post(apiBase + "/blocklist", payload);
};

export const deleteBlocklistEntry = (
  id: number
): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/blocklist/" + id);
};

/** Definição tipada de campo custom (DOC 03 § 4.7 · LAC-071). */
export type ContactFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "boolean"
  | "select"
  | "multiselect";

export type ContactFieldDefinition = {
  id: number;
  slug: string;
  label: string;
  type: ContactFieldType;
  options: string[] | null;
  is_filterable: boolean;
  is_required: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

type ContactFieldDefinitionListResponse = ApiSuccess<ContactFieldDefinition[]> & {
  meta: PaginatedMeta;
};

export const getContactFieldDefinitions = (params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<ContactFieldDefinitionListResponse> => {
  return axios.get(apiBase + "/contact-field-definitions", { params });
};

export const createContactFieldDefinition = (payload: {
  slug: string;
  label: string;
  type: ContactFieldType;
  options?: string[] | null;
  is_filterable?: boolean;
  is_required?: boolean;
  sort_order?: number;
}): Promise<ApiSuccess<ContactFieldDefinition>> => {
  return axios.post(apiBase + "/contact-field-definitions", payload);
};

export const updateContactFieldDefinition = (
  id: number,
  payload: {
    label?: string;
    type?: ContactFieldType;
    options?: string[] | null;
    is_filterable?: boolean;
    is_required?: boolean;
    sort_order?: number;
  }
): Promise<ApiSuccess<ContactFieldDefinition>> => {
  return axios.patch(apiBase + "/contact-field-definitions/" + id, payload);
};

export const deleteContactFieldDefinition = (
  id: number
): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/contact-field-definitions/" + id);
};

export const reorderContactFieldDefinitions = (payload: {
  ordered_ids: number[];
}): Promise<ApiSuccess<null>> => {
  return axios.post(apiBase + "/contact-field-definitions/reorder", payload);
};

/** Resumo higiene — contatos sem interação (DOC 03 § 4.15 · LAC-128/129). */
export type InactiveContactsSummary = {
  eligible_count: number;
  active_count: number;
  eligible_percent: number;
  days: number;
  dashboard_threshold_percent: number;
  inactive_count_api_messages?: boolean;
  /** Até 10 e-mails para preview no modal (U1). */
  preview_emails?: string[];
};

/** Snapshot da última exclusão em lote — undo U5 (TTL ~30d no Redis). */
export type InactiveLastBulkDelete = {
  count: number;
  days: number | null;
  at: string | null;
  contact_ids: number[];
};

export type InactiveContact = {
  id: number;
  email: string;
  name: string | null;
  status: string;
  last_sent_at?: string | null;
  tags?: ContactTag[];
};

type InactiveContactsListResponse = ApiSuccess<InactiveContact[]> & {
  meta: PaginatedMeta;
};

export const getInactiveContactsSummary = (params?: {
  days?: number;
}): Promise<ApiSuccess<InactiveContactsSummary>> => {
  return axios.get(apiBase + "/contacts/inactive/summary", { params });
};

export const getInactiveContacts = (params?: {
  days?: number;
  page?: number;
  per_page?: number;
}): Promise<InactiveContactsListResponse> => {
  return axios.get(apiBase + "/contacts/inactive", { params });
};

export const bulkDeleteInactiveContacts = (payload: {
  days?: number;
  confirm_count?: number;
  contact_ids?: number[];
}): Promise<
  ApiSuccess<{
    deleted_count?: number;
    days: number;
    job_status?: string;
  }>
> => {
  return axios.post(apiBase + "/contacts/inactive/bulk-delete", payload);
};

/** Limiar do card no dashboard (5–80%) + opt-in api_messages — LAC-129/150. */
export const updateInactiveListSettings = (payload: {
  dashboard_threshold_percent?: number;
  inactive_count_api_messages?: boolean;
}): Promise<
  ApiSuccess<{
    dashboard_threshold_percent: number;
    inactive_count_api_messages: boolean;
  }>
> => {
  return axios.patch(apiBase + "/contacts/inactive/settings", payload);
};

/** Tags em massa — IDs ou filtro inactive_days (LAC-149). */
export const bulkApplyContactTags = (payload: {
  contact_ids?: number[];
  tag_ids: number[];
  mode: "append" | "replace";
  filter?: { inactive_days?: number };
}): Promise<ApiSuccess<{ tagged_count: number; mode: string }>> => {
  return axios.post(apiBase + "/contacts/bulk/tags", payload);
};

/** Remove tag auto de hard bounce (IDs ou todos marcados). */
export const clearHardBounceFlag = (payload: {
  contact_ids?: number[];
  all_flagged?: 1 | true;
}): Promise<ApiSuccess<{ cleared_count: number }>> => {
  return axios.post(apiBase + "/contacts/bulk/clear-hard-bounce-flag", payload);
};

/** Libera contatos em quarentena soft (IDs ou todos em quarantine). */
export const releaseQuarantineContacts = (payload: {
  contact_ids?: number[];
  all_quarantined?: 1 | true;
}): Promise<ApiSuccess<{ released_count: number }>> => {
  return axios.post(apiBase + "/contacts/bulk/release-quarantine", payload);
};

/** Limiar do card no dashboard (5–80%) — LAC-129 U4. @deprecated use updateInactiveListSettings */
export const updateInactiveListThreshold = updateInactiveListSettings;

/** Última exclusão de higiene (IDs no cache) — LAC-129 U5. */
export const getInactiveLastBulkDelete = (): Promise<
  ApiSuccess<InactiveLastBulkDelete>
> => {
  return axios.get(apiBase + "/contacts/inactive/last-bulk-delete");
};

/** Restaura vários contatos da lixeira — LAC-129 U5. */
export const bulkRestoreContacts = (payload: {
  contact_ids: number[];
}): Promise<ApiSuccess<{ restored_count: number }>> => {
  return axios.post(apiBase + "/contacts/bulk-restore", payload);
};
